import { NextRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FilterCallbackType, FilterItemType, FilterType } from '../store-locator-filter.interface';

export const useFilterState = (
  filters: { filterGroups?: FilterType[] },
  urlParams: Record<string, string>,
  router: NextRouter
) => {
  const [selectedFilters, setSelectedFilters] = useState<Record<string, FilterItemType[]>>({});

  // Serialize urlParams to avoid unnecessary re-executions
  const urlParamsString = JSON.stringify(urlParams);

  // Initialize filters from URL parameters when component mounts or URL changes
  useEffect(() => {
    const initialFilters: Record<string, FilterItemType[]> = {};
    const parsedUrlParams = JSON.parse(urlParamsString);

    if (Array.isArray(filters?.filterGroups)) {
      filters.filterGroups.forEach((group: FilterType) => {
        // Convert camelCase filter codes to dash-case for URL parameter matching
        const urlParamKey = group.code.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
        const urlValues = parsedUrlParams[urlParamKey]?.split(',') || [];

        // If URL contains values for this filter group, initialize them as selected
        if (urlValues.length > 0) {
          // Find all matching values from the filter group
          const matchingValues = group.values
            .filter((value: FilterItemType) => urlValues.includes(value.code))
            .map((value: FilterItemType) => ({
              ...value,
              selected: true,
            }));

          if (matchingValues.length > 0) {
            initialFilters[group.code] = matchingValues;
          }
        }
      });
    }

    // Update selected filters with initial values from URL
    setSelectedFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.filterGroups, urlParamsString]);

  const handleFiltersChange = (props: FilterCallbackType) => {
    setSelectedFilters((prevFilters) => {
      const entries = Object.entries(props);
      if (entries.length === 0) return prevFilters;

      const [filterKey, filterValue] = entries[0];
      const currentValues = prevFilters[filterKey] || [];

      if (!(filterValue as FilterItemType).selected) {
        // Remove the deselected value
        const newValues = currentValues.filter((item) => item.code !== (filterValue as FilterItemType).code);
        if (newValues.length === 0) {
          // Remove the entire key if no values left
          const { [filterKey]: _, ...rest } = prevFilters;
          return rest;
        }
        return {
          ...prevFilters,
          [filterKey]: newValues,
        };
      }

      // Check if value is already selected
      const isAlreadySelected = currentValues.some((item) => item.code === (filterValue as FilterItemType).code);

      if (isAlreadySelected) {
        return prevFilters;
      }

      // Add new value while preserving existing ones
      return {
        ...prevFilters,
        [filterKey]: [...currentValues, { ...(filterValue as FilterItemType) }],
      };
    });
  };

  const resetFilters = () => {
    if (router && router.push) {
      router.push(
        {
          pathname: router.pathname,
          query: {},
        },
        undefined,
        { shallow: true }
      );
    }
    setSelectedFilters({});
  };

  return { selectedFilters, handleFiltersChange, resetFilters };
};